import { motion } from "framer-motion";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Sparkles } from "lucide-react";
import StarBackground from "../components/StarBackground";
import { useState } from "react";

const translations = {
  en: {
    backToMyWork: "Back to My Work",
    title: "The Horsehead Nebula",
    subtitle: "IC434 - A Dark Silhouette in Orion",
    intro: "The Horsehead Nebula, also known as Barnard 33, is a dark nebula in the constellation Orion. It is located approximately 1,500 light-years from Earth and is one of the most identifiable nebulae because of its distinctive shape resembling a horse's head.",
    skyLocation: "Sky Location",
    skyLocationIntro: "The Horsehead Nebula is located just to the south of Alnitak (ζ Orionis), the easternmost star of Orion's Belt. It is part of the much larger Orion Molecular Cloud Complex.",
    coordinates: "Coordinates (J2000)",
    coordinatesDesc: "Right Ascension (RA) 5h 40m 59s, Declination (Dec) -02° 27′ 30″",
    visibility: "Visibility",
    visibilityDesc: "The Horsehead Nebula is difficult to observe visually due to its dark nature. It requires a large telescope and ideal conditions, though it photographs beautifully with long exposures.",
    bestTime: "Best Time to View",
    bestTimeDesc: "Best observed during winter months in the Northern Hemisphere, when Orion is high in the sky. Peak viewing is between December and February.",
    formation: "Formation",
    formationDesc: "The dark horse head shape is actually a cloud of cold, dense gas and dust silhouetted against the bright emission nebula IC 434 behind it. The nebula was first recorded in 1888 on a photographic plate."
  },
  no: {
    backToMyWork: "Tilbake til Mitt Arbeid",
    title: "Hestehode-tåken",
    subtitle: "IC434 - En Mørk Silhuett i Orion",
    intro: "Hestehode-tåken, også kjent som Barnard 33, er en mørk tåke i stjernebildet Orion. Den ligger omtrent 1500 lysår fra Jorden og er en av de mest gjenkjennelige tåkene på grunn av sin karakteristiske form som ligner et hestehode.",
    skyLocation: "Posisjon på Himmelen",
    skyLocationIntro: "Hestehode-tåken ligger like sør for Alnitak (ζ Orionis), den østligste stjernen i Orions Belte. Den er en del av det mye større Orion Molekylære Skykomplekset.",
    coordinates: "Koordinater (J2000)",
    coordinatesDesc: "Rektascensjon (RA) 5t 40m 59s, Deklinasjon (Dec) -02° 27′ 30″",
    visibility: "Synlighet",
    visibilityDesc: "Hestehode-tåken er vanskelig å observere visuelt på grunn av sin mørke natur. Det krever et stort teleskop og ideelle forhold, selv om den fotograferer vakkert med lang eksponering.",
    bestTime: "Beste Tid for Observasjon",
    bestTimeDesc: "Best observert i vintermånedene på den nordlige halvkule, når Orion står høyt på himmelen. Topp observasjonstid er mellom desember og februar.",
    formation: "Dannelse",
    formationDesc: "Den mørke hestehodformen er faktisk en sky av kald, tett gass og støv som avtegner seg mot den lyse emisjonståken IC 434 bak den. Tåken ble først registrert i 1888 på en fotografisk plate."
  },
  pl: {
    backToMyWork: "Powrót do Mojej Pracy",
    title: "Mgławica Koński Łeb",
    subtitle: "IC434 - Ciemna Sylwetka w Orionie",
    intro: "Mgławica Koński Łeb, znana również jako Barnard 33, to ciemna mgławica w gwiazdozbiorze Oriona. Znajduje się około 1500 lat świetlnych od Ziemi i jest jedną z najbardziej rozpoznawalnych mgławic ze względu na charakterystyczny kształt przypominający głowę konia.",
    skyLocation: "Położenie na Niebie",
    skyLocationIntro: "Mgławica Koński Łeb znajduje się na południe od Alnitak (ζ Orionis), najdalszej na wschód gwiazdy Pasa Oriona. Jest częścią znacznie większego Kompleksu Chmur Molekularnych Oriona.",
    coordinates: "Współrzędne (J2000)",
    coordinatesDesc: "Rektascensja (RA) 5h 40m 59s, Deklinacja (Dec) -02° 27′ 30″",
    visibility: "Widoczność",
    visibilityDesc: "Mgławica Koński Łeb jest trudna do obserwacji wizualnej ze względu na jej ciemną naturę. Wymaga dużego teleskopu i idealnych warunków, chociaż pięknie wychodzi na długich ekspozycjach fotograficznych.",
    bestTime: "Najlepszy Czas na Obserwację",
    bestTimeDesc: "Najlepiej obserwować w miesiącach zimowych na półkuli północnej, gdy Orion jest wysoko na niebie. Szczytowy czas obserwacji to grudzień-luty.",
    formation: "Formowanie",
    formationDesc: "Ciemny kształt końskiej głowy to w rzeczywistości chmura zimnego, gęstego gazu i pyłu, która rysuje się na tle jasnej mgławicy emisyjnej IC 434 za nią. Mgławica została po raz pierwszy zarejestrowana w 1888 roku na płycie fotograficznej."
  }
};

export default function HorseheadDetails() {
  const [searchParams] = useSearchParams();
  const [language, setLanguage] = useState(searchParams.get('lang') || 'en');
  const t = translations[language];

  return (
    <div className="min-h-screen">
      <StarBackground />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/5 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between">
          <Link to="/m31" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors duration-300">
            <ArrowLeft className="w-5 h-5" />
            <span>{t.backToMyWork}</span>
          </Link>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Sparkles className="text-cyan-400 w-6 h-6" />
              <span className="text-xl font-bold" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                {t.title}
              </span>
            </div>
            
            {/* Language Selector */}
            <div className="flex items-center gap-2 ml-4 pl-4 border-l border-white/20">
              <button 
                className={`transition-colors duration-300 px-3 py-1 rounded-full ${
                  language === 'en' 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
                onClick={() => setLanguage('en')}
                data-testid="lang-en-btn"
              >
                EN
              </button>
              <button 
                className={`transition-colors duration-300 px-3 py-1 rounded-full ${
                  language === 'no' 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
                onClick={() => setLanguage('no')}
                data-testid="lang-no-btn"
              >
                NO
              </button>
              <button 
                className={`transition-colors duration-300 px-3 py-1 rounded-full ${
                  language === 'pl' 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
                onClick={() => setLanguage('pl')}
                data-testid="lang-pl-btn"
              >
                PL
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <section className="relative pt-32 pb-24 px-8" data-testid="horsehead-details">
        <div className="max-w-4xl mx-auto">
          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="mb-12"
          >
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6 text-center"
              style={{ fontFamily: 'Unbounded, sans-serif' }}
              data-testid="horsehead-title"
            >
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400">
                {t.title}
              </span>
            </h1>
            <p className="text-lg text-white/90 text-center mb-8">
              {t.subtitle}
            </p>
          </motion.div>

          {/* Content Card */}
          <motion.div 
            className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 md:p-12 shadow-[0_8px_32px_rgba(0,0,0,0.3)] space-y-8"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            {/* Overview */}
            <div>
              <p className="text-white/90 text-lg leading-relaxed mb-6">
                {t.intro}
              </p>
            </div>

            {/* Sky Location */}
            <div className="pt-6 border-t border-white/10">
              <h2 
                className="text-3xl font-bold mb-6"
                style={{ fontFamily: 'Unbounded, sans-serif' }}
              >
                {t.skyLocation}
              </h2>

              <p className="text-white/90 text-lg leading-relaxed mb-6">
                {t.skyLocationIntro}
              </p>

              <div className="space-y-6 text-white/80 leading-relaxed">
                <div>
                  <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                    {t.coordinates}
                  </h3>
                  <p>{t.coordinatesDesc}</p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                    {t.visibility}
                  </h3>
                  <p>{t.visibilityDesc}</p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                    {t.bestTime}
                  </h3>
                  <p>{t.bestTimeDesc}</p>
                </div>
              </div>
            </div>

            {/* Formation Note */}
            <div className="pt-6 border-t border-white/10">
              <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-xl p-6">
                <h3 className="text-xl font-bold text-cyan-300 mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  {t.formation}
                </h3>
                <p className="text-white/80 leading-relaxed">
                  {t.formationDesc}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
